import { useState, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Upload, FileVideo, FileAudio, FileText, Youtube, Instagram, Music2, Play, X, ShieldAlert, ArrowLeftRight } from "lucide-react";
import { ALL_LANGS } from "../lib/api";

const URL_PATTERNS = {
  youtube: /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[\w-]+/,
  instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/(reel|reels|p|tv)\/[\w-]+/,
  tiktok: /^(https?:\/\/)?(www\.|vm\.|vt\.)?tiktok\.com\//,
};

const URL_TAB_META = {
  youtube: {
    Icon: Youtube,
    color: "text-red-500",
    label: "YouTube",
    placeholder: "https://youtu.be/… or https://youtube.com/watch?v=…",
    example: "https://youtu.be/dQw4w9WgXcQ",
  },
  instagram: {
    Icon: Instagram,
    color: "text-pink-500",
    label: "Instagram",
    placeholder: "https://instagram.com/reel/… or /p/…",
    example: "https://instagram.com/reel/Cxyz…",
  },
  tiktok: {
    Icon: Music2,
    color: "text-cyan-400",
    label: "TikTok",
    placeholder: "https://tiktok.com/@user/video/… or vm.tiktok.com/…",
    example: "https://vm.tiktok.com/ZMxxxxxx/",
  },
};

export default function UploadPanel({ onGenerate, processing }) {
  const [tab, setTab] = useState("video");
  const [source, setSource] = useState("auto");
  const [target, setTarget] = useState("english");
  const [narratorGender, setNarratorGender] = useState("auto");
  const [text, setText] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  // Optional user-supplied SRT/VTT — when set, backend skips Whisper.
  const [subsFile, setSubsFile] = useState(null);
  const [subsText, setSubsText] = useState("");
  const [subsFormat, setSubsFormat] = useState("");
  const subsInputRef = useRef(null);

  const handleSubsFile = async (f) => {
    if (!f) { setSubsFile(null); setSubsText(""); setSubsFormat(""); return; }
    const name = (f.name || "").toLowerCase();
    const fmt = name.endsWith(".vtt") ? "vtt" : name.endsWith(".srt") ? "srt" : "";
    if (!fmt) {
      // Silently reject — the UI hint below tells them what's accepted.
      return;
    }
    try {
      const txt = await f.text();
      setSubsFile(f);
      setSubsText(txt);
      setSubsFormat(fmt);
    } catch { /* unreadable — ignore */ }
  };

  const acceptFor = tab === "video" ? "video/*,.mp4,.mov,.webm" : tab === "audio" ? "audio/*,.mp3,.wav,.m4a" : "";
  const sameLang = source !== "auto" && source === target;
  const needsManualSource = source === "auto" && (tab === "text" || (
    (tab === "video" || tab === "audio") && !!subsText
  ));
  const isUrlTab = tab === "youtube" || tab === "instagram" || tab === "tiktok";
  const urlPattern = URL_PATTERNS[tab];
  const urlValid = isUrlTab ? urlPattern.test(mediaUrl.trim()) : false;

  const swap = () => {
    if (source === "auto") return;
    setSource(target);
    setTarget(source);
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
  };

  const handleSubmit = () => {
    if (sameLang || needsManualSource || processing) return;
    if (tab === "text") {
      if (!text.trim()) return;
      onGenerate({ input_type: "text", text_input: text, source_language: source, target_language: target, narrator_gender: narratorGender });
    } else if (isUrlTab) {
      if (!urlValid) return;
      onGenerate({ input_type: tab, media_url: mediaUrl.trim(), source_language: source, target_language: target, narrator_gender: narratorGender });
    } else {
      if (!file) return;
      onGenerate({
        input_type: tab, file,
        source_language: source, target_language: target,
        narrator_gender: narratorGender,
        subtitle_text: subsText || undefined,
        subtitle_format: subsFormat || undefined,
      });
    }
  };

  const canSubmit = (
    tab === "text" ? text.trim().length > 0 :
    isUrlTab ? urlValid :
    !!file
  ) && !sameLang && !needsManualSource;

  return (
    <div className="border border-zinc-800 bg-zinc-900/40" data-testid="upload-panel">
      <div className="p-6 md:p-8 border-b border-zinc-800">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Step 01 — Input
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">
          Choose your source
        </h2>
      </div>

      <div
        data-testid="content-rights-warning"
        className="flex items-start gap-3 px-6 md:px-8 py-4 border-b border-zinc-800 bg-amber-400/5 text-amber-200"
      >
        <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
        <div className="text-xs leading-relaxed">
          Only upload content you own or have permission for. LinguaSub is a tool — you're
          responsible for the rights to what you process. See{" "}
          <a href="/terms" className="underline hover:text-white">Terms</a>.
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => { setTab(v); setFile(null); setMediaUrl(""); }}
        className="w-full"
      >
        <TabsList className="w-full bg-transparent border-b border-zinc-800 rounded-none h-auto p-0 grid grid-cols-3 md:grid-cols-6">
          <TabsTrigger
            value="video"
            data-testid="upload-tab-video"
            className="rounded-none border-r border-zinc-800 data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-none py-4 text-zinc-500 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs"
          >
            <FileVideo className="w-4 h-4 mr-2" /> Video
          </TabsTrigger>
          <TabsTrigger
            value="audio"
            data-testid="upload-tab-audio"
            className="rounded-none border-r border-zinc-800 data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-none py-4 text-zinc-500 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs"
          >
            <FileAudio className="w-4 h-4 mr-2" /> Audio
          </TabsTrigger>
          <TabsTrigger
            value="youtube"
            data-testid="upload-tab-youtube"
            className="rounded-none border-r border-zinc-800 data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-none py-4 text-zinc-500 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs"
          >
            <Youtube className="w-4 h-4 mr-2" /> YouTube
          </TabsTrigger>
          <TabsTrigger
            value="instagram"
            data-testid="upload-tab-instagram"
            className="rounded-none border-r border-zinc-800 data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-none py-4 text-zinc-500 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs"
          >
            <Instagram className="w-4 h-4 mr-2" /> Reels
          </TabsTrigger>
          <TabsTrigger
            value="tiktok"
            data-testid="upload-tab-tiktok"
            className="rounded-none border-r border-zinc-800 data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-none py-4 text-zinc-500 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs"
          >
            <Music2 className="w-4 h-4 mr-2" /> TikTok
          </TabsTrigger>
          <TabsTrigger
            value="text"
            data-testid="upload-tab-text"
            className="rounded-none data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-none py-4 text-zinc-500 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs"
          >
            <FileText className="w-4 h-4 mr-2" /> Text
          </TabsTrigger>
        </TabsList>

        <div className="p-6 md:p-8 space-y-6">
          <TabsContent value="video" className="m-0">
            <DropZone
              file={file}
              accept={acceptFor}
              onFile={handleFile}
              icon={<FileVideo className="w-8 h-8 text-zinc-500" />}
              label="Drop video file (MP4, MOV, WebM)"
              onClear={() => setFile(null)}
              dragOver={dragOver}
              setDragOver={setDragOver}
              inputRef={inputRef}
              testId="video-dropzone"
            />
            {file && (
              <SubsAttachRow
                subsFile={subsFile}
                onFile={handleSubsFile}
                onClear={() => handleSubsFile(null)}
                inputRef={subsInputRef}
              />
            )}
          </TabsContent>
          <TabsContent value="audio" className="m-0">
            <DropZone
              file={file}
              accept={acceptFor}
              onFile={handleFile}
              icon={<FileAudio className="w-8 h-8 text-zinc-500" />}
              label="Drop audio file (MP3, WAV, M4A)"
              onClear={() => setFile(null)}
              dragOver={dragOver}
              setDragOver={setDragOver}
              inputRef={inputRef}
              testId="audio-dropzone"
            />
            {file && (
              <SubsAttachRow
                subsFile={subsFile}
                onFile={handleSubsFile}
                onClear={() => handleSubsFile(null)}
                inputRef={subsInputRef}
              />
            )}
          </TabsContent>
          {["youtube", "instagram", "tiktok"].map((platform) => {
            const meta = URL_TAB_META[platform];
            const active = tab === platform;
            const invalid = active && mediaUrl && !urlValid;
            const PlatformIcon = meta.Icon;
            return (
              <TabsContent key={platform} value={platform} className="m-0">
                <div className="space-y-3">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block">
                    Paste a {meta.label} link
                  </label>
                  <input
                    type="url"
                    value={active ? mediaUrl : ""}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder={meta.placeholder}
                    data-testid={`${platform}-url-input`}
                    className="w-full h-11 bg-zinc-950 border border-zinc-800 rounded-none px-3 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-950 font-mono text-sm"
                  />
                  {invalid && (
                    <div
                      data-testid={`${platform}-url-invalid`}
                      className="text-xs font-mono text-amber-300 border border-amber-400/20 bg-amber-400/5 px-3 py-2"
                    >
                      That doesn't look like a {meta.label} URL. Try something like {meta.example}
                    </div>
                  )}
                  <div className="flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
                    <PlatformIcon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${meta.color}`} />
                    <span>
                      We fetch the audio track only. Please make sure you have the right to translate the clip.
                    </span>
                  </div>
                  <div
                    data-testid={`${platform}-hint`}
                    className="text-[11px] text-zinc-500 border border-zinc-800 bg-zinc-950/40 p-3 leading-relaxed"
                  >
                    <span className="text-amber-300">Heads up:</span> {meta.label} sometimes blocks
                    server-side downloads from cloud IPs. If your link fails, save the clip
                    to your device and upload it on the <span className="text-white">Video</span> tab —
                    it takes the same route through the LinguaSub engine.
                  </div>
                </div>
              </TabsContent>
            );
          })}
          <TabsContent value="text" className="m-0">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste transcript in Hindi, Tamil, Bengali, etc."
              data-testid="text-input-area"
              className="min-h-[220px] bg-zinc-950 border-zinc-800 rounded-none text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 resize-none font-mono text-sm"
            />
          </TabsContent>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-2 items-end">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                Source language
              </div>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger
                  data-testid="source-lang-select"
                  aria-describedby={source === "malayalam" ? "malayalam-accuracy-note" : undefined}
                  className="bg-zinc-950 border-zinc-800 rounded-none h-11 font-mono text-sm text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-950"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none max-h-72">
                  <SelectItem value="auto" data-testid="source-opt-auto" className="font-mono text-sm text-zinc-200 focus:bg-zinc-800 focus:text-white rounded-none">
                    Auto-detect (recommended)
                  </SelectItem>
                  {ALL_LANGS.map((l) => (
                    <SelectItem
                      key={l.key}
                      value={l.key}
                      data-testid={`source-opt-${l.key}`}
                      className="font-mono text-sm text-zinc-200 focus:bg-zinc-800 focus:text-white rounded-none"
                    >
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {source === "malayalam" && (
              <p
                id="malayalam-accuracy-note"
                data-testid="malayalam-accuracy-note"
                className="text-xs font-mono text-zinc-400 leading-relaxed md:row-start-2 md:col-start-1"
              >
                Accuracy may vary for this language. You can manually correct subtitles in the edit screen after processing.
              </p>
            )}

            <div className="flex md:pb-1 justify-center">
              <button
                type="button"
                onClick={swap}
                data-testid="swap-lang-btn"
                disabled={source === "auto"}
                title={source === "auto" ? "Choose a source language to swap" : "Swap source and target"}
                aria-label="Swap languages"
                className="h-11 w-11 border border-zinc-800 hover:border-blue-500 hover:text-blue-400 text-zinc-400 flex items-center justify-center transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            </div>

            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                Target subtitle language
              </div>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger
                  data-testid="target-lang-select"
                  className="bg-zinc-950 border-zinc-800 rounded-none h-11 font-mono text-sm text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-950"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none max-h-72">
                  {ALL_LANGS.map((l) => (
                    <SelectItem
                      key={l.key}
                      value={l.key}
                      data-testid={`target-opt-${l.key}`}
                      className="font-mono text-sm text-zinc-200 focus:bg-zinc-800 focus:text-white rounded-none"
                    >
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="narrator-gender" data-testid="narrator-gender-label" className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block">
              Narrator gender
            </label>
            <Select value={narratorGender} onValueChange={setNarratorGender} disabled={processing}>
              <SelectTrigger
                id="narrator-gender"
                data-testid="narrator-gender-select"
                aria-describedby="narrator-gender-hint"
                className="bg-zinc-950 border-zinc-800 rounded-none h-11 font-mono text-sm text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-950"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid="narrator-gender-options" className="bg-zinc-900 border-zinc-800 rounded-none">
                {[["auto", "Auto-detect"], ["male", "Male"], ["female", "Female"]].map(([value, label]) => (
                  <SelectItem key={value} value={value} data-testid={`narrator-gender-opt-${value}`} className="font-mono text-sm text-zinc-200 focus:bg-zinc-800 focus:text-white rounded-none">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p id="narrator-gender-hint" data-testid="narrator-gender-hint" className="text-xs font-mono text-zinc-400 leading-relaxed">
              Male or Female applies to every sentence in this job. Auto-detect uses text clues for each speaker; unclear cases stay unknown. Retries keep the saved choice.
            </p>
          </div>

          {needsManualSource && (
            <div data-testid="manual-source-required" className="text-xs font-mono text-amber-300 border border-amber-400/20 bg-amber-400/5 px-3 py-2">
              Choose a source language for pasted text or attached subtitles. Auto-detect uses audio transcription.
            </div>
          )}

          {sameLang && (
            <div data-testid="same-lang-warning" className="text-xs font-mono text-amber-300 border border-amber-400/20 bg-amber-400/5 px-3 py-2">
              Source and target are the same. Pick a different language on either side to translate.
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || processing}
            data-testid="generate-btn"
            className="w-full h-12 bg-white text-zinc-950 hover:bg-zinc-200 disabled:bg-zinc-800 disabled:text-zinc-500 font-semibold rounded-none transition-colors duration-200"
          >
            <Play className="w-4 h-4 mr-2" />
            {processing ? "Generating..." : "Generate subtitles"}
          </Button>
        </div>
      </Tabs>
    </div>
  );
}

function DropZone({ file, accept, onFile, icon, label, onClear, dragOver, setDragOver, inputRef, testId }) {
  return (
    <div
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => !file && inputRef.current?.click()}
      className={`border border-dashed ${dragOver ? "border-blue-500 bg-blue-500/5" : "border-zinc-800 bg-zinc-950/50"} p-8 cursor-pointer transition-colors duration-200 min-h-[180px] flex items-center justify-center`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {file ? (
        <div className="w-full flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {icon}
            <div className="min-w-0">
              <div className="text-sm text-white truncate" data-testid="selected-file-name">{file.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </div>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            data-testid="clear-file-btn"
            className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="text-center">
          <div className="inline-flex p-3 border border-zinc-800 mb-3">
            <Upload className="w-5 h-5 text-zinc-500" />
          </div>
          <div className="text-sm text-zinc-300">{label}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2">
            Max 5 GB · Click or drag
          </div>
        </div>
      )}
    </div>
  );
}

function SubsAttachRow({ subsFile, onFile, onClear, inputRef }) {
  // Small optional row: attach an existing .srt/.vtt alongside a video/audio
  // upload so the backend can skip Whisper and just translate the cues.
  return (
    <div
      data-testid="attach-subs-row"
      className="mx-6 md:mx-8 mb-6 border border-dashed border-zinc-800 p-3 flex items-center gap-3"
    >
      <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">
          {subsFile ? "Subtitles attached — Whisper will be skipped" : "Have an existing SRT / VTT?"}
        </div>
        <div className="text-[11px] text-zinc-500 truncate mt-0.5">
          {subsFile
            ? `${subsFile.name} · ${(subsFile.size / 1024).toFixed(1)} KB`
            : "Attach it and we'll translate the cues directly (no transcription)."}
        </div>
      </div>
      {subsFile ? (
        <button
          type="button"
          onClick={onClear}
          data-testid="attach-subs-clear"
          className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white border border-zinc-800 px-3 py-1"
        >
          Remove
        </button>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="attach-subs-browse"
          className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-white border border-zinc-700 px-3 py-1"
        >
          Attach .srt / .vtt
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".srt,.vtt,text/srt,text/vtt"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        className="hidden"
        data-testid="attach-subs-input"
      />
    </div>
  );
}

